import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import config from 'config';
import querystring from 'querystring';
import { parseKitchenSink, parseKitchenSinkRows, parseKitchenSinkHero } from 'models/kitchenSink';
import parseDiff from 'models/diff';
import { parseShortCode, parseShortCodeStatus } from 'models/shortCode';
import { parseHlsToken } from 'models/hlsToken';
import { parseScoring } from 'models/scoring';
import { parseGeoInfo } from 'models/geoInfo';
import { type HttpPayloadBaseObject, Provider } from 'app-types';
import { type HttpClient, QueryOptions } from './api-types';

interface KitchenSinkParams extends HttpPayloadBaseObject {
  device: string;
  menu: string;
  menuQuery?: string;
  rows?: string;
  stations?: string;
  dmaMarket?: string;
}

interface ScoringParams extends HttpPayloadBaseObject {
  transform: string;
  dma?: string;
}

interface ShortCodeParams extends HttpPayloadBaseObject {
  deviceId: string;
  deviceType: string;
  authTypes?: string;
  currentSubscriptions: string;
}

// Config constants
const API_KEY = config.api.apiKey;
const API_URL = config.api.apiUrl;
const API_PATH = config.api.apiPath;
const GEO_API_URL = config.api.geoApiUrl;
const GEO_API_KEY = config.geoApiKey;

const PLATFORM_QUERY = config.api.platformQuery;
const MENU_QUERY = config.api.menuQuery;
const BUILD_PLATFORM = config.buildPlatform;
const KS_VERSION = 'v5';
const DIFF_VERSION = 'v5';

const SHORTCODE_VERSION = 'v1';
const FMS_PROXY_VERSION = 'v1';
const SCORING_VERSION = 'v1';

const queryApi = (
  httpClient: HttpClient,
  url: string,
  options?: QueryOptions,
): Promise<AxiosResponse> => {
  const request: AxiosRequestConfig = {
    url,
    method: options?.data ? 'post' : 'get',
    headers: { 'x-api-key': API_KEY },
  };

  if (options) {
    if (options.params) request.params = options.params;
    request.headers = {
      ...request.headers,
      ...options.headers,
    };
    if (options.data) {
      if (options.contentType === 'JSON') {
        request.data = JSON.stringify(options.data);
        request.headers = {
          ...request.headers,
          'content-type': 'application/json',
        };
      } else {
        request.data = querystring.stringify(options.data as Record<string, string>);
        request.headers = {
          ...request.headers,
          'content-type': 'application/x-www-form-urlencoded',
        };
      }
    }
  }

  return httpClient(request);
};

const queryGeoApi = (httpClient: HttpClient, url: string): Promise<AxiosResponse> => {
  const request: AxiosRequestConfig = {
    url,
    method: 'get',
    headers: { 'x-api-key': GEO_API_KEY },
  };

  return httpClient(request);
};

const getApiUrl = (target: string, version: string, suffix: string = ''): string => {
  return `${API_URL}${API_PATH}/${target}/${version}/${suffix}`;
};

const fetchKitchenSink = async (
  httpClient: HttpClient,
  rowIds?: string[],
): Promise<AxiosResponse | null> => {
  const params: KitchenSinkParams = {
    device: PLATFORM_QUERY,
    menu: MENU_QUERY,
  };

  if (rowIds && rowIds.length > 0) params.rows = rowIds.join();

  let stations: (string | undefined)[] = [];
  let dmaMarket: string | undefined;
  let response: AxiosResponse | null = null;

  try {
    const geoApiResponse = await queryGeoApi(httpClient, GEO_API_URL);
    const { data: result } = geoApiResponse;
    const geoInfo = parseGeoInfo(result);
    stations = geoInfo?.stations || [];
    dmaMarket = geoInfo?.dmaMarket;
  } catch {
    // do nothing
  }

  if (stations && stations.length > 0) {
    params.stations = stations.join(',');
  }

  // The kitchen-sink news `live` row personalizes on dmaMarket, not stations
  // (CBSENTAPPS-1478) — keep sending it alongside stations.
  if (dmaMarket) {
    params.dmaMarket = dmaMarket;
  }

  response = await queryApi(httpClient, getApiUrl('kitchen-sink', KS_VERSION), {
    params,
  });

  return response;
};

const getScoring = async (httpClient: HttpClient, dma?: string): Promise<AxiosResponse> => {
  const params: ScoringParams = { transform: 'ott' };

  if (dma) params.dma = dma;

  const response = await queryApi(httpClient, getApiUrl('vod/scoring', SCORING_VERSION), {
    params,
  });

  return response;
};

export const fetchScoring = async (httpClient: HttpClient) => {
  const response = await getScoring(httpClient);
  try {
    return parseScoring(response.data);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Something Unexpected has gone wrong';
    throw new Error(typeof e === 'string' ? e : message);
  }
};

export const fetchKitchenSinkAll = async (httpClient: HttpClient) => {
  const response = await fetchKitchenSink(httpClient);
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return parseKitchenSink(response.data);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Something Unexpected has gone wrong';
    throw new Error(typeof e === 'string' ? e : message);
  }
};

export const fetchKitchenSinkRows = async (httpClient: HttpClient, rowIds: string[]) => {
  const response = await fetchKitchenSink(httpClient, rowIds);

  try {
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    return {
      // @ts-expect-error
      rows: parseKitchenSinkRows(response.data),
      // @ts-expect-error
      hero: parseKitchenSinkHero(response.data),
    };
    /* eslint-enable @typescript-eslint/ban-ts-comment */
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Something Unexpected has gone wrong';
    throw new Error(typeof e === 'string' ? e : message);
  }
};

export const fetchDiff = async (httpClient: HttpClient, diffMenuId: string | number) => {
  const response = await queryApi(httpClient, getApiUrl('diff', DIFF_VERSION), {
    params: {
      menu: diffMenuId,
    },
  });

  try {
    const result = parseDiff(response);
    return result;
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Something Unexpected has gone wrong';
    throw new Error(typeof e === 'string' ? e : message);
  }
};

export const fetchShortCode = async (
  httpClient: HttpClient,
  deviceId: string,
  authTypes: Provider[],
  currentSubscriptions: Provider[],
) => {
  const deviceType = config.ksDeviceType;
  const params: ShortCodeParams = { deviceId, deviceType } as ShortCodeParams;

  if (authTypes?.length > 0) {
    params.authTypes = authTypes.join();
  }
  params.currentSubscriptions =
    currentSubscriptions?.length > 0 ? currentSubscriptions.join() : 'null';

  const response = await queryApi(httpClient, getApiUrl('shortcode', SHORTCODE_VERSION), {
    params,
  });

  try {
    return parseShortCode(response.data);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Something Unexpected has gone wrong';
    throw new Error(typeof e === 'string' ? e : message);
  }
};

export const fetchShortCodeStatus = async (httpClient: HttpClient, shortCode: string) => {
  const response = await queryApi(httpClient, getApiUrl('shortcode', SHORTCODE_VERSION, 'status'), {
    params: {
      shortcode: shortCode,
    },
  });

  return parseShortCodeStatus(response.data);
};

export const fetchFMS = async (httpClient: HttpClient, ifa: string, lat: string, uuid: string) => {
  let ifatype: string | undefined;
  switch (BUILD_PLATFORM) {
    case 'vizio':
      ifatype = 'vida';
      break;
    case 'lg-webos':
      ifatype = 'ppid';
      break;
    case 'samsung-tizen':
      ifatype = 'tifa';
      break;
    default:
      break;
  }

  const response = await queryApi(httpClient, getApiUrl('fms', 'proxy', FMS_PROXY_VERSION), {
    data: {
      app: {
        bundle: config.fmsBundle,
        googledai: 1,
        coppa: 0,
      },
      device: {
        lat: lat === 'unknown' ? 1 : Number(lat),
      },
      identifiers: {
        ifa: ifa === 'unknown' ? undefined : ifa,
        ifatype,
        userid: uuid,
      },
    },
    contentType: 'JSON',
  });

  return response.data;
};

export const fetchJWTToken = async (
  url: string,
  userId: string | number | null | undefined = null,
  packageCode: string | null | undefined = null,
) => {
  // Chose not to pass in an httpClient here in order to unhinge this from redux
  const response = await queryApi(axios, url, {
    data: {
      userId,
      packageCode,
    },
    contentType: 'JSON',
  });

  return parseHlsToken(response.data);
};
